import { useState } from 'react';
import { HeartIcon } from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import { Button } from '@rocco/components/button';
import type { ButtonProps } from '@rocco/components/button/button';
import { FollowSelector } from './FollowSelector';
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';
import type { PinItemType } from '~/modules/moodboards/type';

interface TileFollowButtonWithPopoverProps extends Omit<
    ButtonProps,
    'onClick'
> {
    /** The item ID. */
    pinId: number;
    /** The library item ID (for products/projects/etc). If not provided, pinId is used. */
    libraryItemId?: number;
    /** The pin type. */
    pinType: PinItemType;
    /** The pin is already followed. Defaults to `false`. */
    isFollowed?: boolean;
}

/**
 * A follow button with an integrated board selector popover.
 * Composes the generic FollowSelector with a heart button trigger.
 */
export const TileFollowButtonWithPopover = ({
    pinId,
    libraryItemId,
    pinType,
    isFollowed = false,
    ...rest
}: TileFollowButtonWithPopoverProps) => {
    const [optimisticFollowed, setOptimisticFollowed] = useState(isFollowed);
    const { isInDesignBoard } = useHandleFollow();

    // Check if already followed (prefer prop, fall back to checking)
    const isActive =
        isFollowed || optimisticFollowed || isInDesignBoard(pinId, pinType);

    return (
        <FollowSelector
            trigger={
                <Button
                    type="button"
                    size="square42"
                    color="secondary70"
                    {...rest}
                >
                    <HeartIcon
                        className={cn(
                            'size-4',
                            isActive ? 'fill-gray-900' : 'stroke-gray-900',
                        )}
                    />
                </Button>
            }
            pinId={pinId}
            libraryItemId={libraryItemId}
            pinType={pinType}
            onFollowChange={(followed) => setOptimisticFollowed(followed)}
        />
    );
};
